// Carrier (logistics company, `courier_company_id`) assignment for shippings (MOOV-5506)
// and tours (MOOV-5507).
// Carrier is NOT 3P: both use courier_company_id, but 3P goes through outsource/insource
// (shippings.mixin.js) and must never be touched from here.
// Plain functions on purpose: shared by the detail cards and the listing collection
// without adding members that could collide with the global mixins.
import Vue from 'vue'
import ApiService from '@/common/api.service'

// The whole carrier feature (shippings and tours) only exists for ecosystems whose front_config
// enables it: `modules.carriers` (same flag GCM2 reads) or a `carriers` key that isn't false/null
export function isCarriersModuleEnabled() {
  const modules = Vue.prototype.$ecosystemModules || {}
  const frontConfig = (Vue.prototype.$ecosystem && Vue.prototype.$ecosystem.front_config) || {}
  return !!modules.carriers || (frontConfig.carriers !== undefined && frontConfig.carriers !== null && frontConfig.carriers !== false)
}

const FINISHED_STATUSES = ['DELIVERED', 'CANCELED', 'RETURNED']

// The detail brings `courier_id`, the listing collection snapshot may only have `courier`
function hasCourier(shipping) {
  return !!(shipping.courier_id || shipping.courier?.id)
}

// null when the carrier can be changed, otherwise the reason it can't
export function carrierBlockReason(shipping) {
  if (shipping.is_outsourced) {
    return 'outsourced'
  }
  if (shipping.shipping_order_id) {
    return 'order'
  }
  if (shipping.tour_id) {
    return 'tour'
  }
  if (FINISHED_STATUSES.includes(shipping.status)) {
    return 'finished'
  }
  // From ATPICKUPPOINT on (INCIDENCE included) the courier holds the package
  if (hasCourier(shipping) && shipping.status !== 'CONFIRMED') {
    return 'courier_has_package'
  }
  return null
}

export function canChangeCarrier(shipping) {
  return carrierBlockReason(shipping) === null
}

// WAITING leaves the pool before getting a carrier (removing it from WAITING doesn't need
// to); CONFIRMED with courier always unassigns the courier first
export function carrierNeedsReady(shipping, companyId) {
  return (shipping.status === 'WAITING' && !!companyId) ||
    (shipping.status === 'CONFIRMED' && hasCourier(shipping))
}

// Status first, then the PATCH. A rejection is the vue-resource Response tagged with the
// failed `step` ('status' | 'patch'); if only the PATCH fails the shipping stays READY.
export async function assignCarrier(shipping, companyId, userId) {
  if (carrierNeedsReady(shipping, companyId)) {
    try {
      await ApiService.post('status', {shipping_id: shipping.id, user_id: userId, status: 'READY'})
    } catch (e) {
      e.step = 'status'
      throw e
    }
  }
  try {
    return await ApiService.update('shippings', shipping.id, {courier_company_id: companyId || null})
  } catch (e) {
    e.step = 'patch'
    throw e
  }
}

// Tours (MOOV-5507): the backend has a dedicated endpoint that moves WAITING to READY by
// itself and, on removal, also unassigns the courier. DRAFT is allowed by the backend but
// kept out here, same as assigning a courier.
const TOUR_ASSIGNABLE_STATUSES = ['READY', 'WAITING']
const TOUR_LOCKED_STATUSES = ['INTRANSIT', 'FINISHED', 'CANCELED']

// null when the carrier can be assigned or changed, otherwise the reason it can't
export function tourCarrierBlockReason(tour) {
  if (!TOUR_ASSIGNABLE_STATUSES.includes(tour.status)) {
    return 'status'
  }
  if (tour.courier_id || tour.assign_courier_id) {
    return 'courier'
  }
  return null
}

export function canRemoveTourCarrier(tour) {
  return !!tour.courier_company_id && !TOUR_LOCKED_STATUSES.includes(tour.status)
}

export function assignTourCarrier(tourId, companyId) {
  return ApiService.post(`tours/${tourId}/courier-company`, {courier_company_id: companyId})
}

export function unassignTourCarrier(tourId) {
  return ApiService.delete(`tours/${tourId}/courier-company`)
}
